import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchQueue, bulkResolveQueueItems, linkQueueItem } from './services/queue.service';
import {
  DEFAULT_PAGE_SIZE,
  QUEUE_VIEW,
  SEARCH_DEBOUNCE_MS,
  getQueueColumns,
} from './constants/lms-course-review.constants';
import { mapQueueItemsToGridRows } from './utils/queue-grid.util';
import { formatLastRefreshedTime } from './utils/lms-course-review.utils';
import {
  getLinkableItemsFromSelection,
  isLinkableWithSuggestion,
  isHighConfidenceLinkable,
  isResolvedReviewStatus,
} from './utils/lms-course-review.utils';

const filtersEqual = (prev = [], next = []) =>
  JSON.stringify(prev) === JSON.stringify(next);

const useLmsCourseReviewHook = ({ onOpenResolution }) => {
  const [queueView, setQueueView] = useState(QUEUE_VIEW.PENDING);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [toast, setToast] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const bulkLinkModalRef = useRef(null);
  const onOpenResolutionRef = useRef(onOpenResolution);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  useEffect(() => {
    onOpenResolutionRef.current = onOpenResolution;
  }, [onOpenResolution]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadQueue = useCallback(async ({ background = false } = {}) => {
    if (!background) {
      setLoading(true);
    }
    setError(null);
    try {
      const data = await fetchQueue({
        page,
        pageSize,
        filters,
        queueView,
        searchTerm: debouncedSearch,
      });
      const rawItems = data.items || [];
      const nextItems =
        queueView === QUEUE_VIEW.HISTORY
          ? rawItems.filter((item) => isResolvedReviewStatus(item.reviewStatus))
          : rawItems;
      setItems(nextItems);
      setTotalCount(queueView === QUEUE_VIEW.HISTORY ? nextItems.length : data.totalCount ?? 0);
      setLastRefreshed(new Date());
    } catch (e) {
      // Surface load failures inline (with a Retry affordance) instead of a
      // transient toast to avoid duplicate, conflicting error signals.
      setError(e);
      setItems([]);
      setTotalCount(0);
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  }, [page, pageSize, filters, queueView, debouncedSearch]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  useEffect(() => {
    setPage(1);
    setSelectedRows([]);
  }, [debouncedSearch]);

  const onPageChange = useCallback((newPage) => {
    setPage(newPage);
    setSelectedRows([]);
  }, []);

  const onPageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setPage(1);
    setSelectedRows([]);
  }, []);

  const onFilterChange = useCallback((newFilters) => {
    const nextFilters = newFilters || [];
    if (filtersEqual(filtersRef.current, nextFilters)) {
      return;
    }
    setFilters(nextFilters);
    setPage(1);
    setSelectedRows([]);
  }, []);

  const onSortChange = useCallback(() => {
    setPage(1);
    setSelectedRows([]);
  }, []);

  const handleCloseToast = useCallback(() => setToast(null), []);

  const openResolutionStable = useCallback(
    (item) => onOpenResolutionRef.current?.(item),
    []
  );

  const buildRowsForIds = useCallback(
    (ids) => {
      const idSet = ids instanceof Set ? ids : new Set(ids);
      return mapQueueItemsToGridRows(items, openResolutionStable, idSet).filter((row) =>
        idSet.has(row.rowData.id)
      );
    },
    [items, openResolutionStable]
  );

  const applyProgrammaticSelection = useCallback(
    (linkableIds) => {
      setSelectedRows(buildRowsForIds(linkableIds));
    },
    [buildRowsForIds]
  );

  const toggleRowSelection = useCallback(
    (itemId) => {
      setSelectedRows((prev) => {
        const isSelected = prev.some((row) => row.rowData?.id === itemId);
        if (isSelected) {
          return prev.filter((row) => row.rowData?.id !== itemId);
        }
        const item = items.find((entry) => entry.id === itemId);
        if (!item || !isLinkableWithSuggestion(item)) {
          return prev;
        }
        return [...prev, ...buildRowsForIds(new Set([itemId]))];
      });
    },
    [items, buildRowsForIds]
  );

  const clearSelection = useCallback(() => {
    setSelectedRows([]);
  }, []);

  const selectedIds = useMemo(() => {
    const ids = (selectedRows || []).map((row) => row.rowData?.id).filter((id) => id != null);
    return new Set(ids);
  }, [selectedRows]);

  const pageLinkableItems = useMemo(
    () => items.filter(isLinkableWithSuggestion),
    [items]
  );

  const pageHighConfidenceItems = useMemo(
    () => items.filter(isHighConfidenceLinkable),
    [items]
  );

  const linkableSelected = useMemo(
    () => getLinkableItemsFromSelection(selectedRows, items),
    [selectedRows, items]
  );

  const selectAllLinkableOnPage = useCallback(() => {
    if (pageLinkableItems.length === 0) return;
    const linkableIds = new Set(pageLinkableItems.map((item) => item.id));
    applyProgrammaticSelection(linkableIds);
  }, [pageLinkableItems, applyProgrammaticSelection]);

  const selectHighConfidenceOnPage = useCallback(() => {
    if (pageHighConfidenceItems.length === 0) return;
    const ids = new Set(pageHighConfidenceItems.map((item) => item.id));
    applyProgrammaticSelection(ids);
  }, [pageHighConfidenceItems, applyProgrammaticSelection]);

  const openBulkLinkModal = useCallback(() => {
    if (linkableSelected.length === 0) return;
    bulkLinkModalRef.current?.toggleModal?.();
  }, [linkableSelected.length]);

  const closeBulkLinkModal = useCallback(() => {
    bulkLinkModalRef.current?.toggleModal?.();
  }, []);

  const onQueueViewChange = useCallback((nextView) => {
    setQueueView(nextView);
    setPage(1);
    setSelectedRows([]);
    setFilters([]);
    setSearchInput('');
  }, []);

  const clearSearch = useCallback(() => {
    setSearchInput('');
  }, []);

  const confirmBulkLink = useCallback(async () => {
    if (linkableSelected.length === 0) return;

    setBulkLoading(true);
    try {
      const response = await bulkResolveQueueItems(linkableSelected);
      const errorCount = Array.isArray(response?.errors) ? response.errors.length : 0;
      setSelectedRows([]);
      closeBulkLinkModal();
      if (errorCount > 0) {
        setToast({
          type: 'error',
          message: `Linked ${linkableSelected.length - errorCount} item(s); ${errorCount} failed (check Canvas writeback).`,
        });
      } else {
        setToast({
          type: 'success',
          message: `Linked ${linkableSelected.length} queue item(s) using suggested matches.`,
        });
      }
      await loadQueue();
    } catch (error) {
      const message =
        error?.message?.includes('writeback') || error?.status === 502
          ? 'Bulk link failed: Canvas writeback error. Items remain pending.'
          : 'Bulk link failed. Please try again.';
      setToast({ type: 'error', message });
    } finally {
      setBulkLoading(false);
    }
  }, [linkableSelected, loadQueue, closeBulkLinkModal]);

  const inlineAccept = useCallback(
    async (item) => {
      const resourceKey = item?.suggestedResource?.resourceKey;
      if (!resourceKey) return;
      setBulkLoading(true);
      try {
        await linkQueueItem(item.id, { resourceKey });
        setToast({
          type: 'success',
          message: `Linked "${item.canvasCourseName}" to ${item.suggestedResource.name}.`,
        });
        await loadQueue();
      } catch (error) {
        const message =
          error?.message?.toLowerCase?.().includes('writeback')
            ? 'Link failed: Canvas writeback error. Item remains pending.'
            : 'Link failed. Please try again.';
        setToast({ type: 'error', message });
      } finally {
        setBulkLoading(false);
      }
    },
    [loadQueue]
  );

  const gridData = useMemo(
    () =>
      mapQueueItemsToGridRows(
        items,
        openResolutionStable,
        selectedIds,
        toggleRowSelection,
        inlineAccept
      ),
    [items, openResolutionStable, selectedIds, toggleRowSelection, inlineAccept]
  );

  const serverSidePaginationProps = useMemo(
    () => ({
      onPageChange,
      onPageSizeChange,
      onSortChange,
      onFilterChange,
      totalRecordsCount: totalCount,
    }),
    [onPageChange, onPageSizeChange, onSortChange, onFilterChange, totalCount]
  );

  const itemCountLabel = totalCount === 1 ? 'item' : 'items';
  const subtitle =
    loading && items.length === 0
      ? 'Loading…'
      : `${formatLastRefreshedTime(lastRefreshed)} · ${totalCount} ${itemCountLabel}`;

  const isHistoryView = queueView === QUEUE_VIEW.HISTORY;

  const columns = useMemo(() => getQueueColumns(isHistoryView), [isHistoryView]);

  return {
    columns,
    gridData,
    loading,
    error,
    page,
    pageSize,
    totalCount,
    subtitle,
    queueView,
    onQueueViewChange,
    isHistoryView,
    searchInput,
    setSearchInput,
    clearSearch,
    onPageChange,
    onPageSizeChange,
    onFilterChange,
    onSortChange,
    serverSidePaginationProps,
    refreshQueue: loadQueue,
    toast,
    setToast,
    handleCloseToast,
    bulkLink: {
      modalRef: bulkLinkModalRef,
      items: linkableSelected,
      loading: bulkLoading,
      open: openBulkLinkModal,
      close: closeBulkLinkModal,
      confirm: confirmBulkLink,
    },
    selection: {
      selectedCount: selectedRows.length,
      linkableCount: linkableSelected.length,
      pageLinkableCount: pageLinkableItems.length,
      pageHighConfidenceCount: pageHighConfidenceItems.length,
      clearSelection,
      selectAllLinkableOnPage,
      selectHighConfidenceOnPage,
      onBulkLinkSuggestions: openBulkLinkModal,
      bulkLoading,
    },
  };
};

export default useLmsCourseReviewHook;
